import React, { useEffect, useState, useMemo, useRef, useCallback } from "react"
import Box from "@mui/material/Box"
import { useInfoStore } from "../../store/userInfo";
import { List, ListItem, Typography } from "@mui/material";
import { useMenuStore } from "../../store/menuState";
import { IItem, IMenu, ICategory } from "../../types/menu";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useOrderStore } from "../../store/order";
import { useTranslation } from "react-i18next";
import { isBrowser, isTablet } from 'react-device-detect';
import { useWindowDimensions } from "../../App";
import moment from "moment";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { styled } from "@mui/material/styles";
import { Analytics } from 'aws-amplify';
import { useAnalytics } from "../../analytics/AnalyticsContext";
import { getCurrentDayIndex } from "../../utils/timeUtils";
import { devLog, menuLog, timerLog, logger } from "../../utils/logger";
import { getCachedImage, setCachedImage, loadMultipleImages } from "../../utils/imageCache";
// import { getImageFromDB } from "../../components/IndexedDB";
// import { useMenuCache } from "../../store/menuCache";

// 플랫폼 확인 Android
const isAndroid = /Android/i.test(navigator.userAgent);
// iOS Safari 확인
const isIOSSafari = /iPhone|iPad|iPod/i.test(navigator.userAgent) && /WebKit/i.test(navigator.userAgent) && !/(CriOS|FxiOS|OPiOS|mercury)/i.test(navigator.userAgent);

// 타이머 관련 로직 개선을 위한 함수
const safeGetTimerInfo = (
  category: ICategory,
  deleteTimerInfo: (categoryId: number) => void,
  setTimerInfo: (categoryId: number, info: any) => void
): ICategory | undefined => {
  if (!category.timeoutId && !category.intervalId && !category.timerCategoryId) {
    return undefined;
  }

  if (category.timeoutEnd && category.timeoutEnd < Date.now()) {
    timerLog(`Category ${category.id} - Timer expired`);
    deleteTimerInfo(category.id);
    return {
      ...category,
      timeoutId: undefined,
      intervalId: undefined,
      timerCategoryId: undefined,
      remainingMinutes: undefined,
      isPreventSelection: false
    };
  }

  if (!category.timeoutEnd) return undefined;

  const remainingTime = category.timeoutEnd - Date.now();
  const remainingMinutes = Math.ceil(remainingTime / 60000);

  // 타이머 정보 업데이트
  setTimerInfo(category.id, {
    timeoutEnd: category.timeoutEnd,
    timerCategoryId: category.id,
    remainingMinutes,
    orderIntervalMinutes: category.orderIntervalMinutes
  });

  return {
    ...category,
    remainingMinutes,
    isPreventSelection: true
  };
};

const IMAGE_URL = process.env.REACT_APP_S3_IMAGE_PAPH;
const badgeText = ['Special', 'Popular', 'New', 'Sale', 'Allergen', 'Spicy', 'Vegan', 'Gluten Free', 'Vegetarian', 'Organic', 'Raw'];
const badgeColor = ['#F72E13', '#FF357E', '#0085FF', '#A95AF8', '#9D5800', '#D00505', '#029370', '#FF8A00', '#60CD2A', '#16BD8C', "#00B2FF"];

/**
 * MIME 타입 추출 함수
 */
const getMimeType = (imagePath: string): string => {
  const extension = imagePath.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    default:
      return "application/octet-stream"; // 기본 MIME 타입
  }
};

export const init = {
  text: '',
  imagePath: "",
  duration: 3000,
  visible: false,
};

const LazyPicture = styled(LazyLoadImage)`
  object-fit: cover;
  width: ${({ width }) => (width ? `${width}px` : "100%")};
  height: ${({ width }) => (width ? `${width}px` : "100%")};

  &.lazy-load-image-background.opacity {
    background-image: none !important;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &.lazy-load-image-background.opacity.lazy-load-image-loaded {
    opacity: 1;
  }
`;

const ItemList = () => {
  const { userInfo, currentLng} = useInfoStore()
  const { menuDao, selectedMenu, checkUseHour, categories, isNativeFlag, setSelectedItem, fetchItems, setSelectedCategory, setTimerInfo, deleteTimerInfo, updateCategoryTimer } = useMenuStore();
  const { width, height, orientation } = useWindowDimensions();
  const { totalGuest, setSnackMode, setShowSnackbar, setLimitValue } = useOrderStore();
  const [imageLoaded, setImageLoaded] = useState(false);
  const base64CacheRef = useRef(new Map<string, string>());
  const mountedRef = useRef(false);
  const renderStartSentRef = useRef(false);
  const renderCompleteSentRef = useRef(false);
  const prevImagePathsRef = useRef<Set<string>>(new Set());
  const storeId = userInfo?.storeId;
  const items = useMenuStore((state) => state.items)
  const selectedCategory = useMenuStore((state) =>  state.selectedCategory)
  const {t} =  useTranslation(`${storeId}`)
  const { recordEvent } = useAnalytics(); 
  const landMode = (orientation === "landscape") ?true: false
  const listRef = useRef<HTMLUListElement>(null);
  const prevCategory = useRef<ICategory | null>(null);
  const scrollPosition = useRef(0);
  const timerIntervalRef = useRef<NodeJS.Timeout>();
  const loadedFlagsRef = useRef(new Map<number, boolean>());
  
  const memoizedMenuHourText = useMemo(() => {
    if(!selectedMenu || !selectedMenu.id) {
      return 'All day, mon-sun'
    }
    
    const item:IMenu = selectedMenu
    const currentTime = moment()
    
    // getCurrentDayIndex 함수를 사용하여 올바른 요일 인덱스 계산
    const tempDayIndex = currentTime.day();
    const tempMenuHour = item.menuHours[tempDayIndex];
    const week = getCurrentDayIndex(currentTime, tempMenuHour?.endHour, tempMenuHour?.startHour);
    
    // console.log('text week',item.menuHours[week])
    if(!item.useHour) return 'All day, mon-sun'
    
    // menuHours 데이터가 없거나 해당 요일의 데이터가 없는 경우
    if(!item.menuHours || !item.menuHours[week]) {
      return 'All day, mon-sun'
    }
    
    const menuHour = item.menuHours[week];
    
    if(menuHour.isOpenAllDay) {
      return 'All day'
    } else if(menuHour.isClosed) {
      menuLog('Unavailable')
      return 'Unavailable'
    } else if (!menuHour.isClosed) {
      const startHour =  currentTime.clone().set({ hour: menuHour.startHour, minute: menuHour.startMinute, second: 0 })
      let end = currentTime.clone().set({ hour: menuHour.endHour, minute: menuHour.endMinute, second: 0 })
      
      // 야간 영업시간 처리 (endHour가 startHour보다 작은 경우)
      if (menuHour.endHour < menuHour.startHour) {
        end.add(1, 'day');
        // 현재 시간이 endHour 이하이면 start도 전날로 조정
        if (currentTime.hour() <= menuHour.endHour) {
          startHour.subtract(1, 'day');
        }
      }
      
      // console.log(`${startHour.format('HH:mm A')}-${end.format('HH:mm A')}`)
      return `${startHour.format('HH:mm A')}-${end.format('HH:mm A')}`
    }
  }, [selectedMenu]);

  // 모든 useEffect와 useMemo를 여기에 배치
  useEffect(() => {
    if(selectedCategory && selectedCategory.id !== 0 && prevCategory.current?.id !== selectedCategory.id) {
      prevCategory.current = selectedCategory;
      const categoryId = selectedCategory.id;
      logger.log("Fetching items for category:", categoryId);
      fetchItems(categoryId).then(() => {
        const listElement = listRef.current;
        if (listElement) {
          listElement.scrollTop = scrollPosition.current;
        }
      });
    }
  }, [selectedCategory?.id]);

  useEffect(() => {
    if (imageLoaded && !isBrowser) {
      const message = JSON.stringify({ type: "renderComplete", timestamp: Date.now() });
      if (window.Android !== undefined) {
        window.Android.postMessage(message);
      } else if (window.ReactNativeWebView !== undefined) {
        window.ReactNativeWebView.postMessage(message);
      }
    }
  }, [imageLoaded]);

  const menuMap = useMemo(() => {
    const map = new Map();
    menuDao.forEach((menu: any) => {
      map.set(menu.id, menu);
    });
    return map;
  }, [menuDao]);

  // 타이머 관련 함수들을 useCallback으로 최적화
  const handleTimerUpdate = useCallback(() => {
    categories.forEach(category => {
      if (category.timeoutId || category.intervalId || category.timerCategoryId) {
        const timerInfo = safeGetTimerInfo(category, deleteTimerInfo, setTimerInfo);
        if (timerInfo) {
          updateCategoryTimer(category.id, timerInfo);
        }
      }
    });
  }, [categories, deleteTimerInfo, setTimerInfo, updateCategoryTimer]);

  useEffect(() => {
    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(handleTimerUpdate, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [handleTimerUpdate]);

  // 모든 이미지 경로
  const allImagePaths = useMemo(() => {
    const paths = new Set<string>();
    if (menuDao) {
      menuDao.forEach((menu: any) => {
        if (menu?.images?.[0]) {
          paths.add(menu.images[0]);
        }
      });
    }
    return Array.from(paths);
  }, [menuDao]);

  // 이미지 변경 감지 / 처리
  useEffect(() => {
    const currentImagePaths = new Set(allImagePaths);
    const prevImagePaths = prevImagePathsRef.current;

    // 변경된 이미지 경로 찾기
    const newPaths = Array.from(currentImagePaths).filter(path => !prevImagePaths.has(path));
    const removedPaths = Array.from(prevImagePaths).filter(path => !currentImagePaths.has(path));

    // 이미지가 변경된 경우에만 처리
    if (newPaths.length > 0 || removedPaths.length > 0) {
      // 제거된 이미지 캐시 정리 (메모리 누수 방지)
      removedPaths.forEach(path => {
        base64CacheRef.current.delete(path);
        // 전역 캐시에서도 제거
        if (typeof getCachedImage === 'function') {
          // 전역 캐시에서도 제거하는 로직이 있다면 추가
        }
      });

      // 새로운 이미지 로드
      if (newPaths.length > 0) {
        // 이미지 로드 시작 전에 renderStart 메시지 전송
        if (!isBrowser && !renderStartSentRef.current) {
          const startMessage = JSON.stringify({ type: "renderStart", timestamp: Date.now() });
          if (window.Android !== undefined) {
            window.Android.postMessage(startMessage);
          }
          renderStartSentRef.current = true;
        }

        // 이미지 로딩을 우선순위로 처리
        const loadPromises = newPaths.map(async (path) => {
          if (isAndroid && window.Android?.getImageAsBase64) {
            try {
              const base64Image = window.Android.getImageAsBase64(path);
              if (base64Image) {
                const mimeType = getMimeType(path);
                const src = `data:${mimeType};base64,${base64Image}`;
                base64CacheRef.current.set(path, src);
              }
            } catch (error) {
              // 에러 발생 시 웹 URL로 fallback
              logger.warn(`Failed to load image as base64: ${path}`, error);
            }
          }
        });

        Promise.all(loadPromises).then(() => {
          // 이미지 로드 완료 후 renderComplete 메시지 전송
          if (!isBrowser && !renderCompleteSentRef.current) {
            const completeMessage = JSON.stringify({ type: "renderComplete", timestamp: Date.now() });
            if (window.Android !== undefined) {
              window.Android.postMessage(completeMessage);
            }
            renderCompleteSentRef.current = true;
          }
        });
      }

      // 현재 이미지 경로 저장
      prevImagePathsRef.current = currentImagePaths;
    }
  }, [allImagePaths]);

  // 초기 이미지 로드
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const loadImages = async () => {
      try {
        // 이미지 로드 시작 전에 renderStart 메시지 전송
        if (!isBrowser && !renderStartSentRef.current) {
          const startMessage = JSON.stringify({ type: "renderStart", timestamp: Date.now() });
          if (window.Android !== undefined) {
            window.Android.postMessage(startMessage);
          }
          renderStartSentRef.current = true;
        }

        const loadPromises = allImagePaths.map(async (path) => {
          if (isAndroid && window.Android?.getImageAsBase64) {
            try {
              const base64Image = window.Android.getImageAsBase64(path);
              if (base64Image) {
                const mimeType = getMimeType(path);
                const src = `data:${mimeType};base64,${base64Image}`;
                base64CacheRef.current.set(path, src);
              }
            } catch (error) {
              // 에러 발생 시 웹 URL로 fallback
              logger.warn(`Failed to load image as base64: ${path}`, error);
            }
          }
        });

        await Promise.all(loadPromises);

        // 이미지 로드 완료 후 renderComplete 메시지 전송
        if (!isBrowser && !renderCompleteSentRef.current) {
          const completeMessage = JSON.stringify({ type: "renderComplete", timestamp: Date.now() });
          if (window.Android !== undefined) {
            window.Android.postMessage(completeMessage);
          }
          renderCompleteSentRef.current = true;
        }

        // 초기 이미지 경로 저장
        prevImagePathsRef.current = new Set(allImagePaths);
      } catch (error) {
        logger.error("Error in image loading process:", error);
      }
    };

    loadImages();
  }, []);

  // 컴포넌트 언마운트 시 캐시 정리
  useEffect(() => {
    return () => {
      // 로컬 캐시 정리
      base64CacheRef.current.clear();
      // 타이머 정리
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  const getBase64ImageSrc = (imagePath: string | null): string => {
    if (!imagePath) return "";

    // 전역 캐시에서 확인
    const cached = getCachedImage(imagePath);
    if (cached) {
      return cached;
    }

    // 로컬 캐시에 이미지가 있는 경우
    if (base64CacheRef.current.has(imagePath)) {
      const localCached = base64CacheRef.current.get(imagePath)!;
      // 전역 캐시에도 저장
      setCachedImage(imagePath, localCached);
      return localCached;
    }

    // 캐시에 없는 경우 웹 URL
    return IMAGE_URL + `/${imagePath}`;
  };

  // storeId가 숫자가 아닌 경우 early return
  if (!storeId || isNaN(Number(storeId))) {
    logger.log('Invalid storeId:', storeId);
    return null;
  }

  const handleOpenDetail = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, item: IItem) => {
    e.stopPropagation();
    e.preventDefault();
    
    if(item.isSoldout) {
      setSnackMode(2)
      setShowSnackbar(true)
      return
    }

    if (!selectedCategory) return;
    
    if(categories[selectedCategory.index].timeoutId || 
      (categories[selectedCategory.index].timerCategoryId !== undefined && 
       categories[selectedCategory.index].timerCategoryId === categories[selectedCategory.index].id)) {
      setLimitValue(categories[selectedCategory.index].orderIntervalMinutes)
      setSnackMode(9)
      setShowSnackbar(true)
      return;
    }

    // Pinpoint 이벤트 기록 (아이템의 실제 카테고리 ID 사용)
    if (!isNativeFlag) {
      recordEvent('item_click', true, {
        itemId: item.itemId,
        itemName: t(`${storeId}:itemNames.${item.itemId}.${currentLng}`),
        categoryId: item.categoryId, // 아이템에 저장된 카테고리 ID 사용
        categoryName: t(`categoryNames.${item.categoryId}.${currentLng}`),
        price: item.itemPrice,
        isSoldout: String(item.isSoldout),
        itemPrice: item.itemPrice,
        maxAllowed: item.maxAllowed || 0,
        minRequired: item.minRequired || 0,
        totalGuest: totalGuest
      });
    } else {
      if (window.Android !== undefined)
        window.Android.postMessage(JSON.stringify({ 
          type: 'attr', 
          data: {
            function: 'attr', 
            itemId: item.itemId, 
            itemName: t(`${storeId}:itemNames.${item.itemId}.${currentLng}`), 
            categoryId: item.categoryId, // 아이템에 저장된 카테고리 ID 사용
            categoryName: t(`categoryNames.${item.categoryId}.${currentLng}`),
            price: item.itemPrice, 
            isSoldout: String(item.isSoldout), 
            itemPrice: item.itemPrice, 
            maxAllowed: item.maxAllowed || 0, 
            minRequired: item.minRequired || 0, 
            totalGuest: totalGuest 
          } 
        }));
    }

    // 서버에서 받은 categoryId 사용
    const itemCategoryId = item.categoryId;
    
    if (!itemCategoryId) {
      logger.error('❌ No categoryId available for item:', { itemId: item.itemId, itemCategoryId: item.categoryId });
      return;
    }
    
    
    logger.log('✅ Setting selected item with item categoryId:', { itemId: item.itemId, categoryId: itemCategoryId });
    setSelectedItem({itemId: item.itemId, categoryId: itemCategoryId})
  }

  const checkLimitText = (item: IItem) => {
    const max = item.isMaxPerPerson? item?.maxAllowed! * totalGuest : item.maxAllowed
    const min = item.minRequired
    if(max && min && min >1) {
      return `Order ${min} to ${max}`
    } else if(max) {
      return `Order up to ${max}`
    } else if(min && min > 1) {
      return `Order at least ${min}`
    }
  }

  // if(!data ) return (
  //   <Portal>
  //     <Loading />
  //   </Portal>
  // )
  // console.log(items)

  return (
    <>
      <Box sx={{ justifyContent: 'flex-start', alignItems: 'flex-start', backgroundColor: '#fff', overflow: 'hidden', width: '100%', height: '100%'}}>
        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography sx={{ml: '1rem', pt: '.9rem', pb: '1rem', fontSize: '2.2rem', fontWeight: '700', lineHeight: 'normal', letterSpacing: '-.022rem', minHeight: '4.8rem'}}>
            {memoizedMenuHourText}
          </Typography>
          <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', mr: '1.5rem' }}>
            {selectedCategory && 
             categories && 
             categories.length > 0 && 
             selectedCategory.index !== undefined && 
             (() => {
               const currentCategory = categories[selectedCategory.index];
               
               // 타이머가 없는 경우 렌더링하지 않음
               if (!currentCategory?.timeoutId && 
                   !currentCategory?.intervalId && 
                   !currentCategory?.timerCategoryId) {
                 return null;
               }
               
               // 카테고리 정보 디버깅 로그 추가
               logger.log('[타이머디버깅]', {
                 카테고리ID: currentCategory?.id,
                 타이머ID: currentCategory?.timeoutId,
                 타이머카테고리ID: currentCategory?.timerCategoryId,
                 남은시간: currentCategory?.remainingMinutes,
                 선택된카테고리ID: selectedCategory.id
               });
               
               // 카테고리 자체가 존재하는지 확인
               if (!currentCategory) return null;
               
               // 카테고리 ID가 선택된 카테고리와 일치하는지 확인
               if (currentCategory.id !== selectedCategory.id) return null;
               
               // 타이머 관련 속성이 있는지 확인 (timeoutId 또는 timerCategoryId)
               const hasTimeoutId = currentCategory.timeoutId !== undefined;
               const hasTimerCategoryId = currentCategory.timerCategoryId !== undefined && 
                                         currentCategory.timerCategoryId === currentCategory.id;
               if (!hasTimeoutId && !hasTimerCategoryId) return null;
               
               // 남은 시간 계산
               if (!currentCategory.timeoutEnd) return null;
               const remainingTime = currentCategory.timeoutEnd - Date.now();
               const remainingMinutes = Math.ceil(remainingTime / 60000);
               
               // 남은 시간이 0이면 표시하지 않음
               if (remainingMinutes <= 0) return null;
                   
               // 모든 조건을 만족하면 타이머 UI 표시
               logger.log("타이머 표시 - 남은 시간:", remainingMinutes, "분");
               return (
                 <Box sx={{ mr: '2.1rem', display: 'flex', flexDirection: 'row', background: '#000',minHeight: '3.1rem', justifyContent: 'center', alignItems: 'center', gap: '0.4rem', paddingX: '.5rem'}}>
                   <img src={process.env.PUBLIC_URL+'/assets/images/time.svg'} style={{ width: '1.8rem', height: '1.8rem', aspectRatio: '1/1'}} alt="time" />
                   <Typography sx={{color: '#fff', fontSize: '1.8rem', fontWeight: '500', lineHeight: 'normal'}}>
                     Order available in {remainingMinutes} mins
                   </Typography>
                 </Box>
               );
             })()
            }
            {selectedCategory && selectedCategory.maxAllowed && selectedCategory.maxAllowed > 0 && (
              <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                <img src={process.env.PUBLIC_URL+'/assets/images/order.svg'} style={{ width: '2.2rem', height: '2.2rem', aspectRatio: '1/1'}} alt="order" />
                <Typography sx={{color: '#555', fontSize: '1.8rem', fontWeight: '600', lineHeight: 'normal',}}>Order up to {selectedCategory.maxAllowed} in this Category</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{display: 'flex', justifyContent: 'center'}}>
          <List 
            ref={listRef}
            sx={{
              backgroundColor: '#fff',
              display: "flex",
              flexDirection: "row",
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              width: '100%',
              maxWidth: "100%",
              flexFlow: "row wrap",
              px: '1rem',
              gap: landMode ? "0rem 1rem": "2rem 1rem",
              height: 'calc(100vh - 4.8rem)',
              overflowY: "auto",
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              paddingBottom:  !isTablet && !isBrowser ? '40rem' : '20.8rem',
              alignContent: 'flex-start',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
              }
            }}>
              {Array.isArray(items.data) && items.data.map((item: IItem, index: number) => {

                // menuDao에서 item.itemId로 menuItem 찾기
                const menuItem = menuMap.get(item.itemId);
                // const blob = new Blob([menuCache[item.images[0]]]);
                // const webImagePath = URL.createObjectURL(blob)//
                const webImagePath = IMAGE_URL + `/${item.images[0]}`;
                // console.log('webImagePath', webImagePath)
                const androidImagePath = menuItem?.images?.[0] ?? null;

                // Android 여부 판단 및 이미지 소스 결정
                // 이미지 소스 결정 Android : getBase64ImageSrc(androidImagePath) web : IMAGE_URL + `/${item.images[0]}`
                const imageSrc = isAndroid
                  ? (androidImagePath ? getBase64ImageSrc(androidImagePath) : webImagePath)
                  : webImagePath;

                // console.log(">>>>>>>>>", menuItem);

                return(
                  // <Box key={index} >
                    <ListItem key={item.itemId} sx={{ 
                      position: 'relative', 
                      flexDirection: 'column', 
                      minWidth: landMode ? '15rem': 'calc(50% - 1rem)', // 세로 모드에서 화면 너비의 50%에서 간격 제외
                      flex: landMode ? 'none' : `0 1 calc(50% - 1rem)`,
                      minHeight: landMode ? '24rem': '23rem', // 세로 모드에서 최소 높이 설정
                      width: landMode ? `calc(((100% - 2rem)/3))` : `calc(50% - 1rem)`, // 세로 모드에서 화면 너비의 50%에서 간격 제외
                      height: landMode ? `calc(((${height/10}rem - 17rem - 4rem)/2.3))` : '23rem', // 세로 모드에서 고정 높이 설정
                      aspectRatio: landMode ? 'auto' : '1.33/1', // 세로 모드에서 가로:세로 비율 1.33:1 설정
                      backgroundColor: '#FFF', 
                      borderRadius: '1.5rem', 
                      boxShadow: 2, 
                      p: '0px', 
                      mb: '1.1rem',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }} onClick={(e) => handleOpenDetail(e, item)}>
                      { item.isSoldout ?
                        <Box sx={[item.isSoldout ? {position: 'absolute', width: '100%', height: '100%', left: 0, top: -1, backgroundColor:'rgba(0, 0, 0, 0.6)'}: {display: 'none'},{ zIndex: 2, borderRadius: '1.5rem'}]}>
                          <Typography sx={{fontSize: '4rem',  textAlign: 'center', marginTop: '30%', fontWeight: 'bold', color: '#fff', textShadowColor: '#000', textShadowRadius: 1, textShadowOffset: {width: 2,height: 2,},}}>
                            Sold Out
                          </Typography>
                        </Box> :
                        <></>

                      // <IconButton aria-label="info" sx={{  position: 'absolute', width: '40px', height: '40px', right: '10px', bottom: '100px' }} onClick={(e) => handleSelectItem(e, item)}>
                      //   <img
                      //     src={process.env.PUBLIC_URL+'/assets/images/ellipse.svg'}
                      //     width={'40px'}
                      //     height={'40px'}
                      //     alt="ellipse"
                      //   />
                      //   <img style={{position: 'absolute', left: '12px', top: '12px'}}
                      //     src={process.env.PUBLIC_URL+'/assets/images/plus.svg'}
                      //     width={'16px'}
                      //     height={'16px'}
                      //     alt="plus"
                      //   />
                      // </IconButton>
                      }
                      <Box>
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          borderRadius: "1.5rem",
                          boxShadow: 2,
                          backgroundColor: item.images[0] ? "transparent" : "#fff", // 이미지 없으면 회색 배경
                        }}
                      >
                        {item.images[0] ? (
                          <LazyPicture
                            sx={{
                              borderTopLeftRadius: "1.5rem",
                              borderTopRightRadius: "1.5rem",
                              width: "100%",
                              height: `62.7%`,
                              objectFit: "cover", // 이미지 비율 유지하며 채우기
                            }}
                            src={imageSrc}
                            onLoad={() => {loadedFlagsRef.current.set(index, true)}}
                          />
                        ): 
                        <Box sx={{display: 'flex', width: '100%', height: '62.7%', background: '#ccc', justifyContent: 'center', alignItems: 'center',
                          borderTopLeftRadius: '1.5rem', borderTopRightRadius: '1.5rem'}}>
                          <Typography sx={{fontSize: '2.4rem',  textAlign: 'left', fontWeight: '500', color: 'rgba(255, 255, 255, 0.80)' ,
                            padding: '2rem', lineHeight: '2.6rem', letterSpacing: '-.024rem'}}>
                            {t(`${storeId}:itemNames.${item.itemId}.${currentLng}`)}
                          </Typography>
                        </Box>}
                      </Box>
                        {/* {!item.images[0] ?
                          <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', top: 0, left: 0,
                            width: '100%', height: '100%',
                            borderRadius: '1.5rem 1.5rem 0rem 0rem', background: '#ccc'}}>
                            <Box sx={{display: 'flex', width: landMode ? '25.4rem' : '21.5rem', height: '10.1rem', justifyContent: 'center', alignItems: 'center', m: '5.9rem 5.8rem 1.4rem 1.3rem'}}>
                              <Typography sx={{fontSize: '2.4rem',  textAlign: 'left', fontWeight: '500', color: 'rgba(255, 255, 255, 0.80)' , lineHeight: '2.6rem', letterSpacing: '-.024rem'}}>
                                {t(`${storeId}:itemNames.${item.itemId}.${currentLng}`)}
                              </Typography>
                            </Box>
                          </Box>
                          :
                          <LazyPicture 
                            sx={{
                              borderTopLeftRadius: "1.5rem",
                              borderTopRightRadius: "1.5rem",
                              height: landMode ? `calc(((100% - 25.4rem - 8rem)/3))`: `calc(((100% - 21.5rem - 2rem)/2))`,
                              width:  landMode ? `calc(((100% - 25.4rem - 8rem)/3))` : `calc(((100% - 21.5rem - 2rem)/2))`,
                              minWidth: landMode ? '33rem': '27rem', minHeight: landMode ? `calc(24rem * .63)`: `calc(23rem * .63)`
                            }}
                            effect="opacity"
                            src={imageSrc}
                            onLoad={() => {
                              setImageLoaded(true);}
                            }
                            // height={'174px'}
                            // src={IMAGE_URL+`/${item.images[0]}`} // use normal <img> attributes as props
                            // width={`${(width - 254)/3 - 20}px`}
                        />
                        } */}
                        { item.badges && (
                          <Box sx={{position: 'absolute', display: 'flex',  gap: '.2rem', top: '.7rem', left: '.3rem'}}>
                          {item.badges.map((value) => {
                            return(
                              <Box sx={{ 
                                position: 'relative', 
                                flex: '0 0 auto', 
                                height: '2.5rem', 
                                padding: '.5rem .5rem', 
                                backgroundColor: badgeColor[value-1], 
                                borderRadius: '30px',
                                // ✅ Flexbox로 완벽한 중앙 정렬
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }} key={value}>
                                <Typography sx={[
                                  {color: '#fff'}, 
                                  { 
                                    fontSize: '1.2rem', 
                                    fontWeight: '700', 
                                    fontStyle: 'normal', 
                                    lineHeight: 'normal', 
                                    textWrap: 'nowrap'
                                  }
                                ]}>
                                  {badgeText[value-1]}
                                </Typography>
                              </Box>
                            )})}
                          </Box>)
                        }
                        {(item.maxAllowed || item.minRequired! > 1) &&
                            <Box sx={{ width: 'auto', backgroundColor: '#000', position: 'absolute', right: '.5rem', top: 'calc(62.7% - 2.7rem)', display: 'flex', alignItems: 'center', justifyContent: 'center', px: '1rem'}}>
                              <Typography sx={{fontSize: '1.4rem', fontWeight: '600', color: '#fff', textAlign: 'center', lineHeight: '2.2rem'}}>{checkLimitText(item)}</Typography>
                            </Box> 
                        }
                      </Box>
                      <Box sx={{position: "absolute",
                          bottom: 0,
                          width: "100%",
                          height: "35%",
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: "center",
                          justifyContent: 'space-between',
                          minHeight: 'fit-content',
                          zIndex: 1,
                          gap: '.5rem',
                          p:'1.0rem 1.4rem'}}>
                        <Typography sx={{ 
                          fontSize: '1.8rem', 
                          width: '100%', 
                          fontWeight: '600', 
                          letterSpacing: '-.018rem', 
                          lineHeight: '2.2rem', 
                          fontStyle: 'normal', 
                          textAlign: 'left',
                          display: '-webkit-box',
                          overflow: 'hidden',
                          minHeight: '4.4rem',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2
                        }}>{t(`${storeId}:itemNames.${item.itemId}.${currentLng}`)}</Typography>
                        <Typography sx={{ fontSize: item.isPriceVisible ? '2.0rem': '1.5rem', width: '100%', fontWeight: '500', letterSpacing: item.isPriceVisible ? '-0.02rem':'-.015rem', fontStyle: 'normal', textAlign:'right', lineHeight: '2.2rem'}}>{item.isPriceVisible ? `$${(item.itemPrice/100).toFixed(2)}` : "Priced by add-ons"}</Typography>
                      </Box>
                    </ListItem>
                  // </Box>
                )
                })
              }
          </List>
        </Box>
      </Box>
    </>
  )
}

export default ItemList


